import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  IconButton,
  Modal,
  ModalDialog,
  Stack,
  Switch,
  Typography,
} from "@mui/joy";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useNSFWStore from "../stores/nsfwStore";

type OnboardingData = {
  nickname?: string;
  source?: string;
  invite_code?: string;
};

const SOURCE_OPTIONS = [
  "friends",
  "tiktok",
  "instagram",
  "facebook",
  "youtube",
  "reddit",
  "google_search",
  "twitter",
  "app_stores",
  "llm",
  "bing_search",
  "online_articles",
  "paid_ad",
  "other",
  "dont_remember",
] as const;

const fieldLabelSx = {
  mb: 1,
  fontWeight: 700,
  color: "common.white",
} as const;

interface OnboardingModalProps {
  open: boolean;
  onClose: () => void;
  onComplete: (data: OnboardingData) => void;
}

export function OnboardingModal({
  open,
  onClose,
  onComplete,
}: OnboardingModalProps) {
  const { t } = useTranslation();
  const sourceDescription = t("onboarding.sourceDescription");
  const [source, setSource] = useState("");
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // NSFW store settings
  const isNSFWEnabled = useNSFWStore((state) => state.isNSFWEnabled);
  const isAgeVerified = useNSFWStore((state) => state.isAgeVerified);
  const setNSFWEnabled = useNSFWStore((state) => state.setNSFWEnabled);
  const setAgeVerified = useNSFWStore((state) => state.setAgeVerified);

  useEffect(() => {
    if (open) {
      setGeneralError(null);
    }
  }, [open]);

  const handleComplete = async () => {
    // Clear previous errors
    setGeneralError(null);

    setIsLoading(true);

    try {
      const data: OnboardingData = {};

      if (source) {
        data.source = source;
      }

      await onComplete(data);
    } catch (error) {
      console.error("Onboarding error:", error);

      // Display error message to user
      if (error instanceof Error) {
        setGeneralError(error.message);
      } else {
        setGeneralError(t("onboarding.error"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = async () => {
    // Clear previous errors
    setGeneralError(null);

    setIsLoading(true);
    try {
      await onComplete({});
    } catch (error) {
      console.error("Onboarding skip error:", error);

      // Display error message to user
      if (error instanceof Error) {
        setGeneralError(error.message);
      } else {
        setGeneralError(t("onboarding.error"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(2px)",
          },
        },
      }}
    >
      <ModalDialog
        sx={{
          width: { xs: "100vw", sm: "min(90vw, 400px)" },
          maxWidth: { xs: "100vw", sm: 400 },
          height: { xs: "100dvh", sm: "auto" },
          maxHeight: { xs: "100dvh", sm: "90vh" },
          m: { xs: 0, sm: 2 },
          px: { xs: 2.5, sm: 3 },
          pt: {
            xs: "max(12px, env(safe-area-inset-top))",
            sm: 3,
          },
          pb: {
            xs: "max(12px, env(safe-area-inset-bottom))",
            sm: 3,
          },
          borderRadius: { xs: 0, sm: "lg" },
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 1.5,
          }}
        >
          <Typography level="title-lg" sx={{ flex: 1, fontSize: "1.1rem" }}>
            {t("onboarding.title")}
          </Typography>
          <IconButton
            onClick={handleSkip}
            size="sm"
            sx={{ p: 0.5 }}
            disabled={isLoading}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Stack spacing={2}>
          <div>
            <Typography level="body-sm" sx={fieldLabelSx}>
              {t("onboarding.source")}
            </Typography>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexWrap: "wrap",
                gap: 0.75,
              }}
            >
              {SOURCE_OPTIONS.map((option) => (
                <Button
                  key={option}
                  size="sm"
                  variant={source === option ? "solid" : "soft"}
                  color={source === option ? "primary" : "neutral"}
                  disabled={isLoading}
                  onClick={() => setSource(option)}
                  sx={{ borderRadius: "999px", fontWeight: 400 }}
                >
                  {t(`onboarding.sourceOptions.${option}`)}
                </Button>
              ))}
            </Box>
            {sourceDescription && (
              <Typography
                level="body-xs"
                sx={{ mt: 0.5, color: "text.tertiary" }}
              >
                {sourceDescription}
              </Typography>
            )}
          </div>

          <div>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography level="body-sm" sx={fieldLabelSx}>
                  {t("onboarding.nsfwEnabled")}
                </Typography>
                <Typography
                  level="body-xs"
                  sx={{ mt: 0.5, color: "text.tertiary" }}
                >
                  {t("onboarding.nsfwEnabledDescription")}
                </Typography>
              </Box>
              <Switch
                checked={isNSFWEnabled}
                onChange={(e) => {
                  if (!isAgeVerified && e.target.checked) {
                    setAgeVerified(true);
                  }
                  setNSFWEnabled(e.target.checked);
                }}
                color={isNSFWEnabled ? "danger" : "neutral"}
                disabled={isLoading}
                sx={{ ml: 2 }}
              />
            </Box>
          </div>
        </Stack>

        {generalError && (
          <Typography
            level="body-sm"
            color="danger"
            sx={{ mt: 2, textAlign: "center" }}
          >
            {generalError}
          </Typography>
        )}

        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          sx={{ mt: 2 }}
        >
          <Button
            onClick={handleComplete}
            loading={isLoading}
            disabled={isLoading}
          >
            {t("onboarding.next")}
          </Button>
        </Stack>
      </ModalDialog>
    </Modal>
  );
}
